import { usePriceDisplayOverhaulTranslations } from '@aboutyou/legacy-shared'
import React from 'react'
import styled, { css } from 'styled-components'
import TinyText from 'uiComponents/atoms/TinyText/TinyText'
import { strikePriceGreyColor } from 'uiComponents/constants/colors'
import { fsXXS, fwMediumBold } from 'uiComponents/constants/typography'

interface IStyledTinyText {
  $withPadding?: boolean
  $inheritColour?: boolean
  $grayedOut?: boolean
}

const StyledTinyText = styled(TinyText)<IStyledTinyText>`
  display: inline-block;
  direction: initial;

  ${({ $withPadding }) =>
    $withPadding &&
    css`
      padding-left: 4px;
    `}

  ${({ $inheritColour }) =>
    $inheritColour &&
    css`
      color: inherit;
    `}

${({ $grayedOut }) =>
    $grayedOut &&
    css`
      color: ${strikePriceGreyColor};
    `}

  &:last-child {
    margin-left: 0px;
  }
`

const FlexContainer = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? 'column-reverse' : 'row')};
  justify-content: center;
  gap: 6px;
`

const PrefixLabel = styled.div`
  font-size: ${fsXXS}px;
  ${fwMediumBold};
  color: ${strikePriceGreyColor};
  line-height: 1.4;
`

const PrefixAndStrikePriceWrapper = styled.span`
  display: flex;
  gap: 4px;
`

interface IPriceBox {
  formattedBasePrice?: string
  formattedSalePrice?: string
  formattedCampaignPrice?: string
  secondaryLabelBasePrice?: string
  secondaryLabelSalePrice?: string
  secondaryLabelCampaignPrice?: string
  className?: string
  displayRrpPrefix?: boolean
  hideDoubleStrikePrice: boolean
}

const SecondaryPriceLabel: React.FC<{
  label: string
  colourInherit?: boolean
}> = ({ label, colourInherit = false }) => (
  <StyledTinyText
    $withPadding
    $grayedOut={!colourInherit}
    $inheritColour={colourInherit}
    $fontSize="10px"
    data-testid="SecondaryLabelCampaignWithSale"
  >
    {label}
  </StyledTinyText>
)

const PriceBox: React.FC<React.PropsWithChildren<IPriceBox>> = props => {
  const {
    formattedSalePrice,
    formattedBasePrice,
    formattedCampaignPrice,
    secondaryLabelBasePrice,
    secondaryLabelSalePrice,
    secondaryLabelCampaignPrice,
    className,
    displayRrpPrefix,
    hideDoubleStrikePrice,
  } = props

  const { rrp: rrpTranslation } = usePriceDisplayOverhaulTranslations()

  if (formattedSalePrice && formattedCampaignPrice) {
    return (
      <FlexContainer
        $reverse={!!secondaryLabelCampaignPrice}
        className={className}
      >
        <StyledTinyText highlight data-testid="ProductPriceCampaignWithSale">
          {formattedCampaignPrice}
          {secondaryLabelCampaignPrice && (
            <SecondaryPriceLabel label={secondaryLabelCampaignPrice} />
          )}
        </StyledTinyText>

        {!hideDoubleStrikePrice && (
          <StyledTinyText striked data-testid="StrikeFormattedSalePrice">
            {formattedSalePrice}
          </StyledTinyText>
        )}

        <PrefixAndStrikePriceWrapper>
          {displayRrpPrefix && <PrefixLabel>{rrpTranslation}</PrefixLabel>}
          <StyledTinyText striked data-testid="StrikeFormattedBasePrice">
            {formattedBasePrice}
          </StyledTinyText>
        </PrefixAndStrikePriceWrapper>
      </FlexContainer>
    )
  }

  if (formattedSalePrice) {
    return (
      <FlexContainer $reverse={!!secondaryLabelSalePrice} className={className}>
        <StyledTinyText highlight data-testid="FormattedSalePrice">
          {formattedSalePrice}
          {secondaryLabelSalePrice && (
            <SecondaryPriceLabel
              label={secondaryLabelSalePrice}
              colourInherit
            />
          )}
        </StyledTinyText>

        <PrefixAndStrikePriceWrapper>
          {displayRrpPrefix && <PrefixLabel>{rrpTranslation}</PrefixLabel>}
          <StyledTinyText striked data-testid="StrikeFormattedBasePrice">
            {formattedBasePrice}
          </StyledTinyText>
        </PrefixAndStrikePriceWrapper>
      </FlexContainer>
    )
  }

  if (formattedCampaignPrice) {
    return (
      <FlexContainer
        $reverse={!!secondaryLabelCampaignPrice}
        className={className}
      >
        <StyledTinyText highlight data-testid="ProductPriceCampaignWithoutSale">
          {formattedCampaignPrice}
          {secondaryLabelCampaignPrice && (
            <SecondaryPriceLabel label={secondaryLabelCampaignPrice} />
          )}
        </StyledTinyText>

        <PrefixAndStrikePriceWrapper>
          {displayRrpPrefix && <PrefixLabel>{rrpTranslation}</PrefixLabel>}
          <StyledTinyText striked data-testid="StrikeFormattedBasePrice">
            {formattedBasePrice}
          </StyledTinyText>
        </PrefixAndStrikePriceWrapper>
      </FlexContainer>
    )
  }

  return (
    <div className={className}>
      <StyledTinyText data-testid="ProductPriceFormattedBasePrice">
        {formattedBasePrice}
        {secondaryLabelBasePrice && (
          <SecondaryPriceLabel label={secondaryLabelBasePrice} />
        )}
      </StyledTinyText>
    </div>
  )
}

export default PriceBox
